// src/pages/TaskPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import styled, { createGlobalStyle, keyframes, css } from "styled-components";
import { format, formatDistanceToNow } from "date-fns";
import { parseDate } from "../utils/helpers/date";
import {
  User,
  DollarSign,
  Calendar,
  MapPin,
  Briefcase,
  Image as ImageIcon,
  Clock,
  Settings,
  Star,
  MessageCircle,
  ArrowLeft,
} from "lucide-react";

import NavBar from "../components/layout/NavBar";
import BackButton from "../components/BackButton";
import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import VerificationBadge from "../components/common/VerificationBadge";

/* =============== DESIGN TOKENS (matching BrowseTasks) =============== */
const theme = {
  colors: {
    primary: "#3b82f6",
    primaryHover: "#2563eb",
    primaryLight: "#eff6ff",
    secondary: "#6366f1",
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#64748b",
    surface: "#ffffff",
    surfaceHover: "#f8fafc",
    background: "#f8fafc",
    border: "#e2e8f0",
    success: "#10b981",
    warning: "#f59e0b",
    danger: "#ef4444",
  },
  gradients: {
    primary: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.06)",
    md: "0 4px 16px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)",
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
  },
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px",
    xxl: "32px",
  },
  borderRadius: {
    sm: "6px",
    md: "12px",
    lg: "16px",
    xl: "20px",
  },
};

/* =============== GLOBAL/ANIM =============== */
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const slideInUp = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;

const GlobalStyle = createGlobalStyle`
  body { 
    background: ${theme.colors.background}; 
    font-family: ${theme.typography.fontFamily}; 
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

/* =============== PAGE STRUCTURE (matching BrowseTasks) =============== */
const Page = styled.div`
  background: ${theme.colors.background};
  min-height: 100vh;
  padding-top: 64px;
  animation: ${fadeIn} 0.6s ease;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Main task details container (matching BrowseTasks Details component)
const Details = styled.main`
  background: ${theme.colors.surface};
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  box-shadow: ${theme.shadows.md};
  max-width: 800px;
  margin: 0 auto;
  animation: ${fadeIn} 0.25s ease;
  position: relative;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

// Sticky header section
const StickyHead = styled.div`
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  backdrop-filter: saturate(180%) blur(20px);
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 32px;
  padding: 24px 32px;
  z-index: 10;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  
  @media (max-width: 768px) {
    padding: 16px 20px;
    margin-bottom: 24px;
  }
  
  a {
    text-decoration: none;
    color: #0f172a;
    font-size: 1.5rem;
    font-weight: 800;
    line-height: 1.3;
    letter-spacing: -0.025em;
    text-transform: capitalize;
    
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }
`;

const TaskStatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin: 0;
  
  ${props => {
    switch (props.status?.toLowerCase()) {
      case 'active':
      case 'open':
        return `
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        `;
      case 'in_progress':
      case 'in-progress':
        return `
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #fde68a;
        `;
      case 'completed':
        return `
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #bfdbfe;
        `;
      case 'cancelled':
      case 'canceled':
        return `
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        `;
      case 'paused':
        return `
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
        `;
      default:
        return `
          background: #f8fafc;
          color: #475569;
          border: 1px solid #e2e8f0;
        `;
    }
  }}
`;

// Poster row components (matching BrowseTasks)
const PosterRow = styled.div`
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #64748b;
  font-size: 0.92rem;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PosterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const ManageTaskButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #64748b;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
  transition: border 0.12s, background 0.12s, color 0.12s;

  &:hover {
    background: #eff6ff;
    border-color: #3b82f6;
    color: #3b82f6;
  }

  @media (max-width: 640px) {
    padding: 6px 10px;
  }
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  background: #eff6ff;
  color: #3b82f6;
  display: grid;
  place-items: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 640px) {
    width: 26px;
    height: 26px;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 32px 0;
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const SoftStat = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fbfdff;
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;

  .label {
    color: #64748b;
    font-weight: 600;
    font-size: 0.9rem;
  }
  .value {
    color: #0f172a;
    font-weight: 800;
    font-size: 0.96rem;
  }

  @media (max-width: 640px) {
    padding: 12px 14px;
    .label {
      font-size: 0.86rem;
    }
    .value {
      font-size: 0.94rem;
    }
  }
`;

const ContentArea = styled.div`
  padding: 0 32px 40px;
  @media (max-width: 1024px) {
    padding: 0 16px 24px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 20px 0 16px;
`;

const Primary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.25);
  transition: transform 0.1s, box-shadow 0.1s;
  
  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #5b5bf6 100%);
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(59, 130, 246, 0.28);
  }
`;

const Ghost = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
  
  &:hover {
    background: #f8fafc;
    color: #475569;
  }
`;

const Section = styled.section`
  padding: 20px 0 28px;
  animation: ${slideInUp} 0.25s ease;

  & + & {
    border-top: 1px dashed #e2e8f0;
    margin-top: 24px;
    padding-top: 30px;
  }

  @media (max-width: 640px) {
    padding: 16px 0 20px;
    & + & {
      margin-top: 14px;
      padding-top: 20px;
    }
  }
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 10px;
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;

  @media (max-width: 640px) {
    font-size: 0.96rem;
    gap: 8px;
    margin-bottom: 8px;
  }
`;

const Body = styled.p`
  color: #475569;
  line-height: 1.7;
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  @media (max-width: 640px) {
    line-height: 1.6;
    font-size: 0.975rem;
  }
`;

const TagWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 640px) {
    gap: 8px;
  }
`;

const TimePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #eff6ff;
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: #3b82f6;
  padding: 8px 12px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.92rem;
  white-space: nowrap;

  @media (max-width: 640px) {
    padding: 7px 10px;
    font-size: 0.9rem;
  }
`;

const Thumb = styled.img`
  height: 84px;
  width: 128px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #e2e8f0;

  @media (max-width: 640px) {
    height: 76px;
    width: 116px;
    border-radius: 8px;
  }
`;

const Empty = styled.div`
  padding: 28px;
  text-align: center;
  color: #64748b;
  border: 1px dashed #e2e8f0;
  border-radius: 14px;
  background: #fafcff;

  @media (max-width: 640px) {
    padding: 18px;
    border-radius: 12px;
  }
`;

const MobileFooterSpacer = styled.div`
  height: 80px;
  @media (min-width: 768px) {
    display: none;
  }
`;

// Form components (matching BrowseTasks)
const OfferFormCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 24px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  animation: ${slideInUp} 0.25s ease;
`;

const FormField = styled.div`
  margin-bottom: 18px;
  label {
    display: block;
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 8px;
    color: #475569;
    & > span.required {
      color: #ef4444;
      margin-left: 2px;
    }
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${props => props.$hasError ? '#ef4444' : '#e2e8f0'};
  background: #fff;
  transition: all 0.2s ease;
  
  ${props => props.$hasError && css`
    animation: shake 0.5s ease-in-out;
  `}
  
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ef4444' : '#3b82f6'};
    box-shadow: ${props => props.$hasError ? 'none' : '0 0 0 4px #eff6ff'};
  }
  
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
  }
`;

const Textarea = styled(Input).attrs({ as: "textarea" })`
  resize: vertical;
  min-height: 110px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const SafetyTipComponent = styled.div`
  background: #eff6ff;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  
  .tip-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    
    .icon {
      font-size: 1.2rem;
    }
    
    .title {
      font-weight: 700;
      color: #1e40af;
      font-size: 0.95rem;
    }
  }
  
  .content {
    color: #1e40af;
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

// Tab components (matching BrowseTasks)
const TabContainer = styled.div`
  display: flex;
  gap: 2px;
  margin: 0 0 24px;
  background: #f8fafc;
  border-radius: 12px;
  padding: 4px;
  box-shadow: inset 0 1px 3px rgba(15, 23, 42, 0.1);
  width: fit-content;
  max-width: 400px;
`;

const TabButton = styled.button`
  background: ${props => props.$active ? '#ffffff' : 'transparent'};
  border: none;
  border-radius: 10px;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 0.9rem;
  color: ${props => props.$active ? '#0f172a' : '#64748b'};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.$active ? '#ffffff' : '#f1f5f9'};
    color: #0f172a;
  }
`;

// Offer and Question cards (matching BrowseTasks)
const OfferCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  animation: ${slideInUp} 0.2s ease;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 8px 32px rgba(15, 23, 42, 0.12);
    transform: translateY(-2px);
  }
`;

const OfferHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const OfferUserInfo = styled.div`
  flex: 1;
`;

const OfferUserName = styled.div`
  font-weight: 700;
  color: #0f172a;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OfferMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 4px;
  font-size: 0.85rem;
  color: #64748b;
`;

const OfferBody = styled.div`
  color: #475569;
  line-height: 1.6;
  margin-bottom: 16px;
`;

const OfferFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const OfferAmount = styled.div`
  font-size: 1.1rem;
  font-weight: 800;
  color: #059669;
`;

const QuestionCard = styled(OfferCard)``;
const QuestionHeader = styled(OfferHeader)``;
const QuestionUserInfo = styled(OfferUserInfo)``;
const QuestionUserName = styled(OfferUserName)``;
const QuestionBody = styled(OfferBody)``;
const QuestionMeta = styled(OfferMeta)``;

const Skeleton = styled.div`
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9, #f8fafc, #f1f5f9);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite linear;
  height: ${(p) => p.h || "14px"};
  width: ${(p) => p.w || "100%"};
  
  @keyframes shimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }
`;

/* =============== FORM COMPONENTS =============== */
// Offer Form Component
const OfferFormComponent = ({ task, onCancel, onOfferSubmitted }) => {
  const [formData, setFormData] = useState({
    rate: "",
    proposal: "",
    duration: "",
    completionDate: "",
    yearsExperience: "",
    previousWork: "",
    portfolioLinks: [""],
    clientQuestions: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePortfolioLinkChange = (i, v) => {
    const links = [...formData.portfolioLinks];
    links[i] = v;
    setFormData((p) => ({ ...p, portfolioLinks: links }));
  };

  const addPortfolioLink = () =>
    setFormData((p) => ({ ...p, portfolioLinks: [...p.portfolioLinks, ""] }));

  const handleSubmit = async () => {
    const errors = {};
    if (!formData.rate || formData.rate.trim() === "") {
      errors.rate = true;
    }
    if (!formData.proposal || formData.proposal.trim() === "") {
      errors.proposal = true;
    }
    
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      setSubmitError("Budget and Proposal are required.");
      return;
    }
    
    setSubmitError("");
    setIsSubmitting(true);
    
    try {
      const payload = {
        task_id: task.id,
        offer_amount: formData.rate,
        proposal: formData.proposal,
        duration: formData.duration,
        completion_date: formData.completionDate,
        years_experience: formData.yearsExperience,
        previous_similar_work: formData.previousWork,
        portfolio_links: formData.portfolioLinks.filter((x) => x.trim()),
        client_questions: formData.clientQuestions,
      };
      
      const { data } = await request("POST", API_CONSTANT_MAP.create_task_offer, payload);
      
      if (data?.success) {
        onOfferSubmitted(data.offer);
        onCancel();
      } else {
        setSubmitError(data.error?.message || "Failed to submit offer. Please try again.");
      }
    } catch (error) {
      setSubmitError("Failed to submit offer. Please try again.");
      console.error('Failed to submit offer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OfferFormCard>
      <SectionTitle>Your Proposal</SectionTitle>
      
      <SafetyTipComponent>
        <div className="tip-header">
          <span className="icon">🛡️</span>
          <span className="title">Stay Safe While Making Offers</span>
        </div>
        <div className="content">
          Read the task details carefully and ask questions if unclear. Never pay upfront or share personal banking information. Keep all communication on the platform.
        </div>
      </SafetyTipComponent>

      {submitError && (
        <div style={{ color: '#ef4444', marginBottom: '16px', fontSize: '0.9rem' }}>
          {submitError}
        </div>
      )}

      <FormRow>
        <FormField>
          <label>
            Your Budget <span className="required">*</span>
          </label>
          <Input
            type="number"
            name="rate"
            value={formData.rate}
            onChange={handleChange}
            placeholder="Enter amount in USD"
            $hasError={validationErrors.rate}
          />
        </FormField>
        <FormField>
          <label>Duration</label>
          <Input
            type="text"
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            placeholder="e.g., 2 weeks"
          />
        </FormField>
      </FormRow>

      <FormRow>
        <FormField>
          <label>Years of Relevant Experience</label>
          <Input
            type="number"
            name="yearsExperience"
            value={formData.yearsExperience}
            onChange={handleChange}
            placeholder="e.g., 5"
          />
        </FormField>
        <FormField>
          <label>Proposed Completion Date</label>
          <Input
            name="completionDate"
            value={formData.completionDate}
            onChange={handleChange}
            type="date"
          />
        </FormField>
      </FormRow>

      <FormField>
        <label>
          Why are you the best fit? <span className="required">*</span>
        </label>
        <Textarea
          name="proposal"
          value={formData.proposal}
          onChange={handleChange}
          placeholder="Describe your approach and why you're the right person for this task..."
          $hasError={validationErrors.proposal}
        />
      </FormField>

      <FormField>
        <label>Describe Previous Similar Work</label>
        <Textarea
          name="previousWork"
          value={formData.previousWork}
          onChange={handleChange}
          placeholder="Briefly describe 1–2 past projects."
        />
      </FormField>

      <FormField>
        <label>Portfolio / Work Samples</label>
        {formData.portfolioLinks.map((link, i) => (
          <Input
            key={i}
            value={link}
            onChange={(e) => handlePortfolioLinkChange(i, e.target.value)}
            placeholder="https://your-portfolio.com"
            style={{ marginBottom: 8 }}
          />
        ))}
        <button
          type="button"
          onClick={addPortfolioLink}
          style={{
            background: 'transparent',
            border: '1px dashed #e2e8f0',
            borderRadius: '8px',
            padding: '8px 12px',
            color: '#64748b',
            cursor: 'pointer',
            fontSize: '0.9rem',
          }}
        >
          + Add Another Link
        </button>
      </FormField>

      <FormField>
        <label>Questions for the Client (optional)</label>
        <Textarea
          name="clientQuestions"
          value={formData.clientQuestions}
          onChange={handleChange}
          placeholder="Anything you need clarified?"
        />
      </FormField>

      <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
        <Primary onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit Offer'}
        </Primary>
        <Ghost onClick={onCancel}>Cancel</Ghost>
      </div>
    </OfferFormCard>
  );
};

// Question Form Component
const QuestionFormComponent = ({ task, onCancel, onQuestionSubmitted }) => {
  const [questionText, setQuestionText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleSubmit = async () => {
    if (!questionText.trim()) {
      setHasError(true);
      return;
    }
    
    setHasError(false);
    setIsSubmitting(true);
    
    try {
      const payload = {
        task_id: task.id,
        question_text: questionText.trim(),
      };
      
      const { data } = await request("POST", API_CONSTANT_MAP.submit_task_question, payload);
      
      if (data?.success) {
        onQuestionSubmitted(data.question);
        onCancel();
      } else {
        console.error('Question submission error:', data.error?.message || "Unknown error");
      }
    } catch (error) {
      console.error('Failed to submit question:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OfferFormCard>
      <SectionTitle>Ask a Question</SectionTitle>
      <FormField>
        <Textarea
          value={questionText}
          onChange={(e) => {
            setQuestionText(e.target.value);
            if (hasError) setHasError(false);
          }}
          placeholder="What would you like to know about this task?"
          style={{ minHeight: "120px" }}
          $hasError={hasError}
        />
      </FormField>
      <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
        <Primary onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit Question'}
        </Primary>
        <Ghost onClick={onCancel}>Cancel</Ghost>
      </div>
    </OfferFormCard>
  );
};

// Activity Component (matching BrowseTasks)
const Activity = ({
  activeTab,
  setActiveTab,
  offers,
  questions = [],
  isOffersLoading,
  isQuestionsLoading,
}) => {
  return (
    <Section id="activity">
      <TabContainer>
        <TabButton
          $active={activeTab === "offers"}
          onClick={() => setActiveTab("offers")}
        >
          Offers ({offers.length})
        </TabButton>
        <TabButton
          $active={activeTab === "qa"}
          onClick={() => setActiveTab("qa")}
        >
          Q & A ({questions?.length || 0})
        </TabButton>
      </TabContainer>
      
      {activeTab === "offers" ? (
        offers.length ? (
          offers.map((offer, i) => (
            <OfferCard key={offer.id} style={{ animationDelay: `${i * 80}ms` }}>
              <OfferHeader>
                <Avatar style={{ width: 32, height: 32 }}>
                  {offer.user?.avatar ? (
                    <img src={offer.user.avatar} alt="User avatar" />
                  ) : (
                    <User size={16} />
                  )}
                </Avatar>
                <OfferUserInfo>
                  <OfferUserName>
                    {offer.user?.first_name && offer.user?.last_name
                      ? `${offer.user.first_name} ${offer.user.last_name}`.trim()
                      : offer.user?.username || "Anonymous User"}
                    <VerificationBadge user={offer.user} />
                  </OfferUserName>
                  <OfferMeta>
                    <span>{formatDistanceToNow(parseDate(offer.created_at), { addSuffix: true })}</span>
                    {offer.years_experience && (
                      <span>{offer.years_experience} years experience</span>
                    )}
                  </OfferMeta>
                </OfferUserInfo>
              </OfferHeader>
              
              <OfferBody>{offer.proposal}</OfferBody>
              
              <OfferFooter>
                <OfferAmount>${offer.offer_amount}</OfferAmount>
                {offer.duration && (
                  <span style={{ color: '#64748b', fontSize: '0.9rem' }}>
                    Duration: {offer.duration}
                  </span>
                )}
              </OfferFooter>
            </OfferCard>
          ))
        ) : (
          <Empty>No offers yet — be the first to pitch!</Empty>
        )
      ) : (
        <div>
          {questions?.length ? (
            questions.map((q, i) => (
              <QuestionCard key={q.id} style={{ animationDelay: `${i * 80}ms` }}>
                <QuestionHeader>
                  <Avatar style={{ width: 40, height: 40 }}>
                    {q.avatar ? (
                      <img src={q.avatar} alt="User avatar" />
                    ) : (
                      <User size={20} />
                    )}
                  </Avatar>
                  <QuestionUserInfo>
                    <QuestionUserName>
                      {q.user_name || "Anonymous User"}
                      <VerificationBadge user={q} />
                    </QuestionUserName>
                    <QuestionMeta>
                      <span>{formatDistanceToNow(parseDate(q.created_at), { addSuffix: true })}</span>
                    </QuestionMeta>
                  </QuestionUserInfo>
                </QuestionHeader>
                <QuestionBody>{q.question_text}</QuestionBody>
                {q.replies?.length > 0 && (
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                    {q.replies.map((reply, idx) => (
                      <div key={idx} style={{ marginBottom: '12px' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '4px' }}>
                          {reply.user_name} replied:
                        </div>
                        <div style={{ color: '#475569', fontSize: '0.9rem' }}>
                          {reply.reply_text}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </QuestionCard>
            ))
          ) : (
            <Empty>No questions yet — be the first to ask!</Empty>
          )}
        </div>
      )}
    </Section>
  );
};

/* =============== PAGE =============== */
function TaskPage({ state }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form states (matching BrowseTasks)
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  
  // Tab and data states
  const [activeTab, setActiveTab] = useState("offers");
  const [offers, setOffers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [isOffersLoading, setIsOffersLoading] = useState(false);
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(false);

  // Helper function to check if task is cancelled
  const isTaskCancelled = task?.status?.toLowerCase() === 'cancelled' || task?.status?.toLowerCase() === 'canceled';

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const resp = await request("POST", API_CONSTANT_MAP.fetch_single_task, {
          task_id: id,
        });
        if (resp.data?.success) setTask(resp.data.task);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Reset form states when task changes
  useEffect(() => {
    setActiveTab("offers");
    setShowOfferForm(false);
    setShowQuestionForm(false);
  }, [task?.id]);

  // Fetch offers and questions when task loads
  useEffect(() => {
    if (task?.id) {
      fetchOffers();
      fetchQuestions();
    }
  }, [task?.id]);

  const fetchOffers = async () => {
    if (!task?.id) return;
    setIsOffersLoading(true);
    try {
      const { data } = await request("POST", API_CONSTANT_MAP.fetch_task_offers, {
        task_id: task.id,
      });
      if (data?.success) {
        setOffers(data.offers || []);
      }
    } catch (error) {
      console.error('Failed to fetch offers:', error);
    } finally {
      setIsOffersLoading(false);
    }
  };

  const fetchQuestions = async () => {
    if (!task?.id) return;
    setIsQuestionsLoading(true);
    try {
      const { data } = await request("POST", API_CONSTANT_MAP.fetch_task_questions, {
        task_id: task.id,
      });
      if (data?.success) {
        setQuestions(data.questions || []);
      }
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setIsQuestionsLoading(false);
    }
  };

  const addNewOffer = (offer) => {
    setOffers(prev => [offer, ...prev]);
  };

  const addNewQuestion = (question) => {
    setQuestions(prev => [question, ...prev]);
  };

  if (loading) {
    return (
      <>
        <GlobalStyle />
        <Page>
          <NavBar user={state.user} />
          <Container>
            <Details>
              {/* Sticky header skeleton */}
              <StickyHead>
                <div>
                  <Skeleton h="20px" w="20px" style={{ borderRadius: "4px" }} />
                </div>
                <div>
                  <Skeleton h="24px" w="60%" style={{ borderRadius: "6px" }} />
                </div>
                <PosterRow>
                  <PosterInfo>
                    <Skeleton h="16px" w="80px" style={{ borderRadius: "4px" }} />
                    <Skeleton h="32px" w="32px" style={{ borderRadius: "50%" }} />
                    <Skeleton h="16px" w="100px" style={{ borderRadius: "4px" }} />
                  </PosterInfo>
                </PosterRow>
              </StickyHead>

              <ContentArea>
                {/* Stats skeleton */}
                <StatGrid>
                <SoftStat>
                  <Skeleton h="18px" w="18px" />
                  <div>
                    <Skeleton h="12px" w="50px" style={{ marginBottom: "4px" }} />
                    <Skeleton h="16px" w="60px" />
                  </div>
                </SoftStat>
                <SoftStat>
                  <Skeleton h="18px" w="18px" />
                  <div>
                    <Skeleton h="12px" w="60px" style={{ marginBottom: "4px" }} />
                    <Skeleton h="16px" w="80px" />
                  </div>
                </SoftStat>
                <SoftStat>
                  <Skeleton h="18px" w="18px" />
                  <div>
                    <Skeleton h="12px" w="55px" style={{ marginBottom: "4px" }} />
                    <Skeleton h="16px" w="90px" />
                  </div>
                </SoftStat>
                </StatGrid>

                {/* Button row skeleton */}
                <ButtonRow>
                  <Skeleton h="38px" w="130px" style={{ borderRadius: "10px" }} />
                  <Skeleton h="38px" w="140px" style={{ borderRadius: "10px" }} />
                </ButtonRow>

                {/* Description skeleton */}
                <Section>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                  <Skeleton h="18px" w="18px" />
                  <Skeleton h="18px" w="150px" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <Skeleton h="16px" w="100%" />
                  <Skeleton h="16px" w="95%" />
                  <Skeleton h="16px" w="87%" />
                  <Skeleton h="16px" w="60%" />
                </div>
              </Section>

              {/* Additional section skeleton */}
              <Section>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                  <Skeleton h="18px" w="18px" />
                  <Skeleton h="18px" w="100px" />
                </div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <Skeleton h="36px" w="120px" style={{ borderRadius: "999px" }} />
                  <Skeleton h="36px" w="140px" style={{ borderRadius: "999px" }} />
                </div>
                </Section>
              </ContentArea>
            </Details>
          </Container>
        </Page>
      </>
    );
  }

  if (!task) {
    return (
      <Page>
        <NavBar user={state.user} />
        <Container>
          <Details>
            <ContentArea>
              <Empty>Task not found or could not be loaded.</Empty>
            </ContentArea>
          </Details>
          <MobileFooterSpacer />
        </Container>
      </Page>
    );
  }

  const isPoster = state.user?.user_id === task.poster?.id;

  return (
    <>
      <GlobalStyle />
      <Page>
        <NavBar user={state.user} />
        <Container>
          <Details>
            {/* Sticky header */}
            <StickyHead>
              <div>
                <BackButton onClick={() => navigate("/tasks/browse")} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <a href={`/tasks/${task.id}`} style={{ textTransform: "capitalize", textDecoration: "none", color: "#0f172a", fontSize: "1.5rem", fontWeight: 800, lineHeight: 1.3, letterSpacing: "-0.025em" }}>
                  {task.title}
                </a>
                {task.status && (
                  <TaskStatusBadge status={task.status}>
                    {task.status.replace('_', ' ')}
                  </TaskStatusBadge>
                )}
              </div>
              <PosterRow>
                <PosterInfo>
                  <span>
                    Posted{" "}
                    {formatDistanceToNow(parseDate(task.created_at), {
                      addSuffix: true,
                    })}{" "}
                    by
                  </span>
                  <Avatar>
                    {task.poster?.avatar ? (
                      <img src={task.poster.avatar} alt="Poster avatar" />
                    ) : (
                      <User size={16} />
                    )}
                  </Avatar>
                  <span
                    style={{
                      color: "#0f172a",
                      textDecoration: "none",
                      transition: "text-decoration 0.2s ease",
                      fontWeight: 700,
                      cursor: "pointer",
                      textTransform: "capitalize",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.textDecoration = "underline";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.textDecoration = "none";
                    }}
                    onClick={() => {
                      window.location.href = `/users/${task.poster?.id}`;
                    }}
                  >
                    {task.poster?.first_name && task.poster?.last_name
                      ? `${task.poster.first_name} ${task.poster.last_name}`
                      : task.poster?.username || "User"}
                  </span>
                  <VerificationBadge user={task.poster} />
                </PosterInfo>
                {/* Show Manage Task button if user is the poster */}
                {isPoster && (
                  <ManageTaskButton onClick={() => navigate(`/tasks/${task.id}/manage`)}>
                    <Settings size={14} /> Manage Task
                  </ManageTaskButton>
                )}
              </PosterRow>
            </StickyHead>

            <ContentArea>
              <StatGrid>
                <SoftStat>
                  <div>
                    <DollarSign size={18} />
                  </div>
                  <div>
                    <div className="label">Budget</div>
                    <div className="value">${task.budget}</div>
                  </div>
                </SoftStat>

                <SoftStat>
                  <div>
                    <MapPin size={18} />
                  </div>
                  <div>
                    <div className="label">Location</div>
                    <div className="value">
                      {task.task_type === "online"
                        ? "Remote"
                        : task.location || "Location not specified"}
                    </div>
                  </div>
                </SoftStat>

                {task.due_date && (
                  <SoftStat>
                    <div>
                      <Calendar size={18} />
                    </div>
                    <div>
                      <div className="label">Deadline</div>
                      <div className="value">
                        {format(parseDate(task.due_date), "MMM d, yyyy")}
                      </div>
                    </div>
                  </SoftStat>
                )}
              </StatGrid>
              <ButtonRow>
                <Primary
                  onClick={() => {
                    if (isTaskCancelled) return;
                    if (!state.user?.user_id) {
                      navigate("/signin");
                      return;
                    }
                    setShowOfferForm(true);
                    setShowQuestionForm(false);
                    setActiveTab("offers");
                  }}
                  disabled={isTaskCancelled}
                  style={{
                    opacity: isTaskCancelled ? 0.5 : 1,
                    cursor: isTaskCancelled ? 'not-allowed' : 'pointer'
                  }}
                  title={isTaskCancelled ? 'This task has been cancelled' : 'Submit an offer for this task'}
                >
                  <Star size={16} /> Submit an Offer
                </Primary>
                <Ghost
                  onClick={() => {
                    if (isTaskCancelled) return;
                    if (!state.user?.user_id) {
                      navigate("/signin");
                      return;
                    }
                    setShowQuestionForm(true);
                    setShowOfferForm(false);
                    setActiveTab("qa");
                  }}
                  disabled={isTaskCancelled}
                  style={{
                    opacity: isTaskCancelled ? 0.5 : 1,
                    cursor: isTaskCancelled ? 'not-allowed' : 'pointer'
                  }}
                  title={isTaskCancelled ? 'This task has been cancelled' : 'Ask a question about this task'}
                >
                  <MessageCircle size={16} /> Ask a Question
                </Ghost>
              </ButtonRow>

              {showOfferForm && (
                <div style={{ marginTop: 14 }}>
                  <OfferFormComponent
                    task={task}
                    onCancel={() => setShowOfferForm(false)}
                    onOfferSubmitted={(offer) => {
                      addNewOffer(offer);
                      setShowOfferForm(false);
                    }}
                  />
                </div>
              )}

              {showQuestionForm && (
                <div style={{ marginTop: 14 }}>
                  <QuestionFormComponent
                    task={task}
                    onCancel={() => setShowQuestionForm(false)}
                    onQuestionSubmitted={(question) => {
                      addNewQuestion(question);
                      setShowQuestionForm(false);
                    }}
                  />
                </div>
              )}

              {/* Description */}
              <Section>
                <SectionTitle>
                  <Briefcase size={18} /> Project Description
                </SectionTitle>
                <Body>{task.description}</Body>
              </Section>

              {/* Time Windows */}
              {task.time_windows?.length > 0 && (
                <Section>
                  <SectionTitle>
                    <Clock size={18} /> Availability
                  </SectionTitle>
                  <TagWrap>
                    {task.time_windows.map((tw) => (
                      <TimePill key={tw.id}>
                        {tw.name}: {tw.start_time} – {tw.end_time}
                      </TimePill>
                    ))}
                  </TagWrap>
                </Section>
              )}

              {/* Attachments */}
              {task.images?.length > 0 && (
                <Section>
                  <SectionTitle>
                    <ImageIcon size={18} /> Attachments
                  </SectionTitle>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {task.images.map((img) => (
                      <Thumb key={img.id} src={img.url} alt="Task Attachment" />
                    ))}
                  </div>
                </Section>
              )}

              <Activity
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                offers={offers}
                questions={questions}
                isOffersLoading={isOffersLoading}
                isQuestionsLoading={isQuestionsLoading}
              />
            </ContentArea>
          </Details>
          <MobileFooterSpacer />
        </Container>
      </Page>
    </>
  );
}

const mapStateToProps = (state) => ({ state });
export default connect(mapStateToProps)(TaskPage);
